/**
 * JSBridge 状态管理模块
 * 提供全局 JSBridge 状态管理, 支持状态监听和初始化
 */

import { ref, computed, watch } from 'vue';
import { getAppInfo } from '@/utils/native';
import { isDev } from '@/utils/env';
import { isAndroidEnv } from '@/utils';

/** JSBridge 状态类型 */
type JSBridgeState = 'idle' | 'checking' | 'ready' | 'error';

/** JSBridge 属性名 */
const JS_BRIDGE_NAME = 'appJsBridge';

/** 全局 JSBridge 状态 */
const jsBridgeState = ref<JSBridgeState>('idle');

/** 便利的计算属性 */
export const isJSBridgeReady = computed(() => jsBridgeState.value === 'ready');
export const isJSBridgeLoading = computed(
    () => jsBridgeState.value === 'checking' || jsBridgeState.value === 'idle',
);
export const isJSBridgeError = computed(() => jsBridgeState.value === 'error');

/**
 * 监听 window.appJsBridge 属性的变化 (内部函数)
 * @param handleReady - 属性值变化时触发的回调
 * @param handleTimeout - 超时时触发的回调
 * @param timeout - 超时时间, 默认 1000ms
 */
const waitForAppJsBridge = (
    handleReady?: () => void,
    handleTimeout?: () => void,
    timeout = 1000,
) => {
    const timer = setTimeout(() => {
        console.log(`${JS_BRIDGE_NAME} timeout`);
        handleReady = () => {}; // 取消 handleReady
        handleTimeout && handleTimeout();
    }, timeout);

    let proxyAttr = window[JS_BRIDGE_NAME];
    Object.defineProperty(window, JS_BRIDGE_NAME, {
        get: () => proxyAttr,
        set: (newVal) => {
            proxyAttr = newVal;
            if (newVal) {
                console.log(
                    `${JS_BRIDGE_NAME} ready: ${JSON.stringify(newVal)}`,
                );
                clearTimeout(timer);
                handleReady && handleReady();
            }
        },
        configurable: true,
    });
};

/**
 * 检查 JSBridge 是否正常工作
 * 通过获取应用信息验证是否为 pictorial 应用
 */
const checkJSBridge = () => {
    const appInfo = getAppInfo()?.identify;
    jsBridgeState.value = appInfo === 'pictorial' ? 'ready' : 'error';
};

/**
 * 初始化 JSBridge 状态检查
 */
export const initJSBridge = () => {
    // 防止重复检查
    if (jsBridgeState.value !== 'idle') return;
    jsBridgeState.value = 'checking';

    // 开发环境直接返回 ready 状态
    if (isDev) {
        jsBridgeState.value = 'ready';
        return;
    }

    // 非安卓环境直接返回错误状态
    if (!isAndroidEnv()) {
        console.log('非安卓环境, 直接返回错误状态');
        jsBridgeState.value = 'error';
        return;
    }

    // JSBridge 已注入则直接检查, 否则监听注入
    if (window[JS_BRIDGE_NAME]) {
        checkJSBridge();
    } else {
        waitForAppJsBridge(checkJSBridge, () => {
            jsBridgeState.value = 'error';
        });
    }
};

/**
 * 等待 JSBridge 就绪的 Promise
 * @returns Promise<boolean> - true: 就绪, false: 错误
 */
export const waitForJSBridgeReady = (): Promise<boolean> => {
    return new Promise((resolve) => {
        // 如果已经就绪, 直接返回
        if (jsBridgeState.value === 'ready') {
            resolve(true);
            return;
        }

        // 如果已经错误, 直接返回 false
        if (jsBridgeState.value === 'error') {
            resolve(false);
            return;
        }

        // 监听状态变化 - 状态必定会在 initJSBridge 的 1000ms 内确定
        const stopWatcher = watch(jsBridgeState, (newState) => {
            if (newState === 'ready') {
                stopWatcher();
                resolve(true);
            } else if (newState === 'error') {
                stopWatcher();
                resolve(false);
            }
        });
    });
};
